/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 17th 2022, 12:21:58 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Grid from "@mui/material/Grid";
import ShareIcon from "@mui/icons-material/Share";
import { ProcessDefinition } from "lib/schemas";
import { useProcessDefinitionsAPI } from "service-builder/services/ProcessDefinitionAPI";
import { OSActionButton, StyledOSDialog, StyledOSDialogProps } from "ui-widgets";
import { ShareFormSubmissionLink } from "form-builder/components/ShareFormSubmissionLink";

interface ShareServiceFormsLinksDialogProps {
  children: StyledOSDialogProps["renderOpenButton"];
  processDefinition: ProcessDefinition;
}

export const ShareServiceFormsLinksDialog = ({ children, processDefinition }: ShareServiceFormsLinksDialogProps) => {
  const { serviceForms, showShareDialog, showMultiFormSelect } = useShareServiceFormsLinksDialog({ processDefinition });

  return (
    <>
      {showShareDialog ? (
        <>
          {showMultiFormSelect ? (
            <StyledOSDialog
              popUpTitle="Share Links to Service Forms"
              popUpIcon={<ShareIcon color="primary" />}
              renderOpenButton={children}
              open={null}
              maxWidth="xs"
            >
              <Grid container sx={{ padding: 3 }} rowSpacing={2}>
                {serviceForms.map((submissionForm) => (
                  <ShareFormSubmissionLink
                    key={submissionForm.slug}
                    formSlug={submissionForm.slug}
                    formTitle={submissionForm.title}
                    formType={submissionForm.form_type}
                    processDefinitionId={processDefinition.id}
                    processDefinitionSiteURL={processDefinition.site_url}
                  >
                    {({ handleOpen }) => (
                      <>
                        <Grid item md={12}>
                          <OSActionButton variant="text" onClick={handleOpen}>
                            Share {submissionForm.title} Link
                          </OSActionButton>
                        </Grid>
                      </>
                    )}
                  </ShareFormSubmissionLink>
                ))}
              </Grid>
            </StyledOSDialog>
          ) : (
            <ShareFormSubmissionLink
              formSlug={serviceForms[0]?.slug}
              formTitle={serviceForms[0]?.title}
              formType={serviceForms[0]?.form_type}
              processDefinitionId={processDefinition.id}
              processDefinitionSiteURL={processDefinition.site_url}
            >
              {({ handleOpen }) => children({ handleOpen })}
            </ShareFormSubmissionLink>
          )}
        </>
      ) : null}
    </>
  );
};

const useShareServiceFormsLinksDialog = ({
  processDefinition,
}: Pick<ShareServiceFormsLinksDialogProps, "processDefinition">) => {
  const { useAPIProcessDefinitionSubmissionForms, useAPIProcessDefinitionServiceForms } = useProcessDefinitionsAPI();

  const { items: submissionForms, fetchSubmissionForms } = useAPIProcessDefinitionSubmissionForms();
  const { items: serviceRegistrationForms, fetchServiceForms } = useAPIProcessDefinitionServiceForms();

  React.useEffect(() => {
    if (!processDefinition?.id) return;

    fetchSubmissionForms(processDefinition.id);
    fetchServiceForms(processDefinition.id);
  }, [processDefinition?.id, fetchSubmissionForms, fetchServiceForms]);

  const serviceForms =
    submissionForms?.length && serviceRegistrationForms?.length
      ? submissionForms.concat(serviceRegistrationForms)
      : submissionForms || serviceRegistrationForms;

  const showShareDialog = processDefinition?.id && serviceForms?.length > 0;

  const showMultiFormSelect = serviceForms?.length > 1;

  return { serviceForms, showShareDialog, showMultiFormSelect };
};
